import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { trackEvent as trackEventHelper } from '@zola-helpers/client';

// Redux
import * as actions from '../../../reducers/pdp/actions';
import * as selectors from '../../../reducers/pdp/selectors';
import { getPricing } from '../../../reducers/pricing/selectors';
import { getPDPCardIsLetterpress } from '../../../selectors/letterpress';

// Components
import OptionSection from '../OptionSection/OptionSection';
import { openModal } from '../../../actions/modalActions';
import LetterpressPaperOption from '../../common/Letterpress/LetterpressPaperOption/LetterpressPaperOption';

// Utils
import {
  getEnabledOptionsValuesList,
  enrichCardLevelOptions,
  enrichOptionsAccSelection,
  sectionIsHidden,
  getCurrentOptionDimension,
  getCurrentOptionLabel,
  getOptionPricing,
  isComparisonModuleHidden,
  getTooltipMessagePerOption,
} from './utils';

const sortedOptionsKeys = [
  'color',
  'type',
  'size',
  'finish',
  'foil-color',
  'paper-type',
  'silhouette',
];

export const AllOptionsSection = ({
  activeMediumKey,
  activeSelection,
  availableOptions,
  activeSelectionSize,
  cardType,
  isStampFoilCard,
  isMagnet,
  isLetterpress,
  onShowCardsModal,
  pricing,
  setOptionValue,
  isAdminView,
  location,
  paperTypeModalOptions,
  paperTypeModalProps = {},
  isDigital,
  condensed,
}) => {
  const sizePricing = getOptionPricing(
    pricing,
    cardType,
    activeMediumKey,
    activeSelection.size,
    'size'
  );
  const handleOptionLinkClick = useCallback(() => {
    onShowCardsModal('PAPER_TYPE', paperTypeModalProps, paperTypeModalOptions);

    trackEventHelper('CTA Recommendation Clicked', {
      recommendation_key: 'Paper Types Description Modal Opened',
      recommendation_name: 'See Paper Types Description',
      recommendation_copy: 'Compare Our Paper Types',
      location,
      section: 'PAPER_OPTIONS',
    });
  }, [onShowCardsModal, paperTypeModalOptions, paperTypeModalProps]);

  if (!availableOptions) {
    return null;
  }

  const optionKeys = isDigital ? ['color'] : sortedOptionsKeys;

  return (
    <>
      {optionKeys.map(sortedOptionsKey => {
        const uiData = availableOptions[sortedOptionsKey];

        if (!uiData) {
          return null;
        }

        const { key, options, label } = uiData;

        const selection = options.find(o => o.value === activeSelection[sortedOptionsKey]);
        const enabledOptionsValuesList = getEnabledOptionsValuesList(options, cardType, key);
        const cardTypeOptions = enrichCardLevelOptions(
          key,
          options,
          cardType,
          enabledOptionsValuesList,
          activeMediumKey,
          isAdminView
        );
        let enrichedOptions = enrichOptionsAccSelection(
          key,
          cardTypeOptions,
          activeSelection,
          cardType,
          pricing,
          activeMediumKey
        );

        const optionType = key;
        if (
          sectionIsHidden(
            optionType,
            isStampFoilCard,
            cardType,
            enrichedOptions,
            activeSelection,
            isLetterpress
          )
        ) {
          return null;
        }

        // Render custom paper type option section for letterpress cards
        if (isLetterpress && key === 'paper-type') {
          return <LetterpressPaperOption key={key} condensed={condensed} hideOptionsOnMobile />;
        }

        // Hide postcard option for letterpress cards
        if (isLetterpress && key === 'size') {
          enrichedOptions = enrichedOptions.filter(option => option.value !== 'postcard');
        }

        return (
          <OptionSection
            key={key}
            cardType={cardType}
            title={label}
            options={enrichedOptions}
            selection={{ type: key, ...selection }}
            setOptionValue={setOptionValue}
            currentLabel={getCurrentOptionLabel({ selection, optionType, cardType })}
            currentDimension={getCurrentOptionDimension(selection, optionType)}
            sizePricing={sizePricing}
            optionType={optionType}
            isMagnet={isMagnet}
            isLinkHidden={isComparisonModuleHidden(cardType, enabledOptionsValuesList)}
            condensed={condensed}
            titleLink="Compare Our Paper Types"
            titleLinkAction={handleOptionLinkClick}
            tooltipMessage={getTooltipMessagePerOption(optionType, activeSelectionSize, isMagnet)}
          />
        );
      })}
    </>
  );
};

AllOptionsSection.propTypes = {
  isAdminView: PropTypes.bool,
  location: PropTypes.string,
  paperTypeModalOptions: PropTypes.shape({}),
  paperTypeModalProps: PropTypes.shape({}),
  isDigital: PropTypes.bool,
  condensed: PropTypes.bool,
};

export default connect(
  state => ({
    activeMediumKey: selectors.getActiveMediumKey(state),
    activeSelection: selectors.getActiveSelection(state),
    availableOptions: selectors.getAvailableOptions(state),
    activeSelectionSize: selectors.getActiveSelectionSize(state),
    cardType: selectors.getCardType(state),
    isStampFoilCard: selectors.getIsStampFoilCard(state),
    isMagnet: selectors.getIsMagnet(state),
    isLetterpress: getPDPCardIsLetterpress(state),
    pricing: getPricing(state),
  }),
  {
    setOptionValue: actions.setOptionValue,
    onShowCardsModal: openModal,
  }
)(AllOptionsSection);
